import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from '../components/layout/Sidebar';
import { Header } from '../components/layout/Header';
import { FeedbackHost } from '../components/layout/FeedbackHost';
import { useNotificationsSocket } from '../hooks/useNotificationsSocket';
import clsx from 'clsx';

/**
 * The frame every signed-in profile shares. A workspace only decides which menu
 * the sidebar shows.
 *
 * Two different menu behaviours, because the sidebar cannot be both:
 * - **From `md` up** it holds a column of its own, and `collapsed` is the user's
 *   standing choice between the full menu and icons.
 * - **Below that** it is a drawer over the page: 240px of a 400px screen is not
 *   a column, it is the screen.
 *
 * @param {object} props
 * @param {Array<object>} props.navigation - See `Sidebar`.
 * @param {string} [props.areaLabel] - See `Sidebar`.
 * @param {object|null} [props.areaSwitch] - See `Sidebar`.
 * @param {boolean} [props.showUpload=true] - See `Sidebar`.
 */
export const AppShell = ({ navigation, areaLabel, areaSwitch = null, showUpload = true }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  // Mounted here, above the pages, so an export keeps being watched after the
  // user navigates away from the page that started it.
  useNotificationsSocket();

  // Navigating closes the drawer: it covers the page it just opened.
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    <div className="min-h-screen flex bg-ninja-gray dark:bg-[#0f0f0f]">
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        navigation={navigation}
        areaLabel={areaLabel}
        areaSwitch={areaSwitch}
        showUpload={showUpload}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
      />

      {mobileOpen && (
        <div
          aria-hidden="true"
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-overlay"
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden"
        />
      )}

      {/* `min-w-0` is what lets this column shrink. A flex item defaults to
          `min-width: auto`, meaning it refuses to go below the width of its
          content — so without this, one wide row (the status chips, a table)
          stretches the whole layout and pushes even the header off screen,
          even when that row carries its own overflow-x-auto.

          The margin only exists from `md` up, where the sidebar takes a column,
          and it matches its width exactly: at 220 against a 240 sidebar, the
          page spent 20px underneath it. */}
      <div className={clsx(
        "flex-1 min-w-0 flex flex-col transition-all duration-300",
        collapsed ? "md:ml-[80px]" : "md:ml-[240px]"
      )}>
        <Header onOpenMenu={() => setMobileOpen(true)} />
        <main className="p-4 sm:p-8">
          <Outlet />
        </main>
      </div>

      <FeedbackHost />
    </div>
  );
};
